import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {strings} from "@khan/guide-utils";

import {LoadingDots} from "../loading-dots.tsx";

import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";
import timerIcon from "@phosphor-icons/core/regular/timer.svg";

interface Props {
    /**
     * A boolean indicating whether there is an error associated with the
     * message.
     */
    isError?: boolean;

    /**
     * A countdown in seconds until the next retry attempt.
     */
    retryTimeoutSeconds: number;

    /**
     * A boolean indicating whether the message is being sent to the server.
     */
    isSending?: boolean;

    /**
     * An optional style override. Mostly used to override positioning of the
     * component in its rendered context.
     */
    style?: StyleType;
}

/**
 * Renders a status label below the MessageBubble component to communicate the
 * current state of the message (sending, error, and high traffic)
 */
export const MessageBubbleOutgoingStatus = (
    props: Props,
): React.ReactElement => {
    const {isError, isSending, retryTimeoutSeconds, style} = props;

    if (isSending && retryTimeoutSeconds <= 0) {
        return (
            <View style={[styles.neutral, style]}>
                <LabelXSmall>{t`Sending`}</LabelXSmall>
                <Strut size={spacing.xxxSmall_4} />
                <LoadingDots
                    color={semanticColor.core.foreground.neutral.subtle}
                    size="small"
                />
            </View>
        );
    }

    if (isError) {
        return (
            <View style={[styles.error, style]}>
                <PhosphorIcon size="small" icon={warningCircleIcon} />
                <Strut size={spacing.xxxSmall_4} />
                <LabelXSmall role="alert">{t`Not delivered`}</LabelXSmall>
            </View>
        );
    }

    if (retryTimeoutSeconds > 0) {
        return (
            <View style={[styles.neutral, style]}>
                <LabelXSmall>
                    {strings.respondingInNSeconds(retryTimeoutSeconds)}
                </LabelXSmall>
                <Strut size={spacing.xxxSmall_4} />
                <PhosphorIcon icon={timerIcon} />
            </View>
        );
    }

    return <></>;
};

const styles = StyleSheet.create({
    neutral: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    error: {
        color: semanticColor.core.foreground.critical.default,
    },
});
